import React, { useEffect, useState } from 'react';
import { X, Copy, Check, Wallet, Building2, QrCode, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

/**
 * BuyATBalanceModal — lets users purchase AT (1 INR = 1 AT) by paying to admin's
 * UPI / QR / Bank, then submitting the transaction reference. Admin approves later.
 */
const BuyATBalanceModal = ({ open, onClose }) => {
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState('upi');
  const [amount, setAmount] = useState('');
  const [txnRef, setTxnRef] = useState('');
  const [payerName, setPayerName] = useState('');
  const [payerAcc, setPayerAcc] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState('');
  const [history, setHistory] = useState([]);
  const [screenshotUrl, setScreenshotUrl] = useState('');
  const [uploadingShot, setUploadingShot] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/at-deposit/settings').then(r => setSettings(r.data.settings)).catch(() => {});
    api.get('/at-deposit/my').then(r => setHistory(r.data.deposits || [])).catch(() => {});
  }, [open]);

  const copy = (label, value) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopied(label);
    setTimeout(() => setCopied(''), 1500);
  };

  const submit = async () => {
    if (!amount || Number(amount) < (settings?.min_amount || 10)) {
      toast.error(`Minimum ₹${settings?.min_amount || 10}`); return;
    }
    if (Number(amount) > (settings?.max_amount || 100000)) {
      toast.error(`Maximum ₹${settings?.max_amount || 100000}`); return;
    }
    if (!txnRef || txnRef.trim().length < 4) {
      toast.error('Transaction Reference / UTR must be at least 4 characters (e.g. 12-digit UPI Ref number from your payment app)', { duration: 6000 });
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post('/at-deposit/create', {
        amount: Number(amount),
        method: tab,
        txn_ref: txnRef.trim(),
        payer_name: payerName || null,
        payer_upi_or_account: payerAcc || null,
        screenshot_url: screenshotUrl || null,
        notes: notes || null,
      });
      toast.success(`Request submitted! ₹${amount} → ${r.data.deposit.at_equivalent} AT after admin approval (usually within 30 min)`, { duration: 6000 });
      setAmount(''); setTxnRef(''); setPayerName(''); setPayerAcc(''); setNotes(''); setScreenshotUrl('');
      const r2 = await api.get('/at-deposit/my');
      setHistory(r2.data.deposits || []);
    } catch (e) {
      // FastAPI may return detail as array (Pydantic validation) or string
      const raw = e.response?.data?.detail;
      let msg = 'Submit failed. Please check your details and try again.';
      if (Array.isArray(raw)) {
        msg = raw.map(x => `${(x.loc || []).slice(-1)[0]}: ${x.msg}`).join(' · ');
      } else if (typeof raw === 'string' && raw) {
        msg = raw;
      } else if (e.message) {
        msg = e.message;
      }
      toast.error(msg, { duration: 7000 });
    } finally { setSubmitting(false); }
  };

  const uploadScreenshot = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Max 8 MB'); return; }
    setUploadingShot(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      // Make URL absolute so admin (different origin) can open it
      const url = r.data.url?.startsWith('http') ? r.data.url : `${BACKEND}${r.data.url}`;
      setScreenshotUrl(url);
      toast.success('Screenshot uploaded ✓');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploadingShot(false); }
  };

  if (!open) return null;
  const s = settings || {};

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 p-3 flex items-start sm:items-center justify-center overflow-y-auto" onClick={onClose} data-testid="buy-at-modal">
      <div className="my-6 w-full max-w-2xl bg-slate-900 rounded-2xl border-2 border-amber-500/40 shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-5 border-b border-amber-500/20 bg-gradient-to-r from-amber-950 to-slate-900 rounded-t-2xl">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-black" />
              </div>
              <div>
                <h2 className="text-xl font-extrabold text-amber-300">Buy AT Balance</h2>
                <p className="text-xs text-slate-400">Rate: <span className="text-amber-300 font-semibold">1 INR = 1 AT</span> · Instant after admin approval</p>
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="buy-at-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 💡 AT Kaise Kamayen? — Real earning paths (Feb 2026: free bonus removed, only 3 legit ways) */}
        <div className="px-5 pt-4" data-testid="at-how-to-earn-section">
          <div className="rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-950/40 to-slate-900/60 p-3.5">
            <div className="text-xs font-bold text-emerald-300 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> AT Kaise Kamayen? · 3 Real Ways
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs">
              <div className="rounded-lg bg-slate-950/70 border border-slate-800 p-2.5" data-testid="earn-way-purchase">
                <div className="text-amber-300 font-bold mb-0.5">1. Purchase (Direct)</div>
                <div className="text-slate-300 leading-snug">₹ deposit karo · <span className="text-amber-200 font-semibold">1 INR = 1 AT</span> · Instant after admin approval</div>
              </div>
              <div className="rounded-lg bg-slate-950/70 border border-slate-800 p-2.5" data-testid="earn-way-referral">
                <div className="text-cyan-300 font-bold mb-0.5">2. Direct Referral Commission</div>
                <div className="text-slate-300 leading-snug">Aapke direct referral ne subscription/pack liya → aapko <span className="text-cyan-200 font-semibold">commission split</span> milta hai (7-tier Affiliate Network webchain)</div>
              </div>
              <div className="rounded-lg bg-slate-950/70 border border-slate-800 p-2.5" data-testid="earn-way-subsplit">
                <div className="text-fuchsia-300 font-bold mb-0.5">3. Subscription Split</div>
                <div className="text-slate-300 leading-snug">Har paid subscription (Trial Room, Vyapar, AVX, etc.) ka <span className="text-fuchsia-200 font-semibold">33% Affiliate Network pool</span> upline ko auto-split hota hai</div>
              </div>
            </div>
            <div className="mt-2 text-[10px] text-rose-300/80 leading-snug">
              🚫 <span className="font-semibold">Free signup bonus band hai</span> — koi bhi AT sirf real purchase / commission / split se aata hai. No fake bonus, no shortcuts — sirf pure earning.
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-800">
          {['upi', 'qr', 'bank'].map(t => (
            <button
              key={t}
              data-testid={`buy-at-tab-${t}`}
              onClick={() => setTab(t)}
              className={`flex-1 px-3 py-3 text-sm font-semibold capitalize inline-flex items-center justify-center gap-2 ${tab === t ? 'bg-amber-500/10 text-amber-300 border-b-2 border-amber-400' : 'text-slate-400 hover:text-slate-200'}`}
            >
              {t === 'upi' && <Sparkles className="w-4 h-4" />}
              {t === 'qr' && <QrCode className="w-4 h-4" />}
              {t === 'bank' && <Building2 className="w-4 h-4" />}
              {t.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Payment Details */}
        <div className="p-5 space-y-4">
          {tab === 'upi' && (
            <div className="bg-slate-950 rounded-xl border border-amber-500/20 p-4 space-y-3">
              <div className="text-xs text-slate-400 uppercase tracking-wider">Pay to this UPI ID</div>
              <div className="flex items-center justify-between gap-3 bg-slate-900 rounded-lg p-3">
                <code className="text-amber-300 font-mono text-base sm:text-lg break-all" data-testid="buy-at-upi-id">{s.upi_id || '—'}</code>
                <button onClick={() => copy('upi', s.upi_id)} className="px-3 py-1.5 rounded-md bg-amber-500 text-black text-xs font-bold inline-flex items-center gap-1.5">
                  {copied === 'upi' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied === 'upi' ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="text-xs text-slate-300">Payee: <span className="font-semibold text-slate-100">{s.upi_payee_name}</span></div>
            </div>
          )}

          {tab === 'qr' && (
            <div className="bg-slate-950 rounded-xl border border-amber-500/20 p-4 text-center">
              <div className="text-xs text-slate-400 uppercase tracking-wider mb-3">Scan this QR with any UPI app</div>
              <img
                src={`${BACKEND}${s.qr_image_url || '/api/at-deposit/default-qr.jpg'}`}
                alt="UPI QR"
                className="mx-auto w-64 h-64 object-contain rounded-lg bg-white p-2"
                data-testid="buy-at-qr-image"
              />
              <div className="text-xs text-slate-300 mt-3">UPI ID: <code className="text-amber-300">{s.upi_id}</code></div>
            </div>
          )}

          {tab === 'bank' && (
            <div className="bg-slate-950 rounded-xl border border-amber-500/20 p-4 space-y-3">
              <div className="text-xs text-slate-400 uppercase tracking-wider">Bank Transfer (NEFT/IMPS/RTGS)</div>
              {[
                ['Account Holder', s.bank_account_name],
                ['Account Number', s.bank_account_number],
                ['IFSC Code', s.bank_ifsc],
                ['Bank Name', s.bank_name],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-3 bg-slate-900 rounded-lg p-2.5">
                  <div>
                    <div className="text-[10px] text-slate-400 uppercase">{k}</div>
                    <div className="text-sm font-mono text-amber-200">{v || '—'}</div>
                  </div>
                  <button onClick={() => copy(k, v)} className="px-2.5 py-1 rounded-md bg-slate-700 text-amber-200 text-xs inline-flex items-center gap-1">
                    {copied === k ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Instructions */}
          {s.instructions && (
            <div className="text-xs text-slate-300 bg-slate-950/60 border border-slate-800 rounded-lg p-3 whitespace-pre-line">
              {s.instructions}
            </div>
          )}

          {/* Form */}
          <div className="space-y-3 pt-2">
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1">Amount paid (₹)</label>
              <input
                data-testid="buy-at-amount-input"
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder={`Min ₹${s.min_amount || 10} · Max ₹${s.max_amount || 100000}`}
                className="w-full px-3 py-2.5 rounded-lg bg-slate-950 border border-slate-700 text-slate-100 focus:border-amber-400 focus:outline-none"
              />
              {amount && Number(amount) > 0 && (
                <div className="text-xs text-emerald-300 mt-1">→ You will receive <span className="font-bold">{Number(amount)} AT</span> after approval</div>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1">Transaction Reference (UTR / UPI Ref / Bank Ref) *</label>
              <input
                data-testid="buy-at-txnref-input"
                type="text"
                value={txnRef}
                onChange={e => setTxnRef(e.target.value)}
                placeholder="e.g. 123456789012 (min 4 chars)"
                className={`w-full px-3 py-2.5 rounded-lg bg-slate-950 border ${txnRef && txnRef.trim().length < 4 ? 'border-rose-500' : 'border-slate-700'} text-slate-100 focus:border-amber-400 focus:outline-none`}
              />
              {txnRef && txnRef.trim().length < 4 ? (
                <div className="text-xs text-rose-400 mt-1">⚠️ Minimum 4 characters. Copy the UTR / UPI Ref number from your payment app (usually 10–12 digits).</div>
              ) : (
                <div className="text-[10px] text-slate-500 mt-1">Find this in your UPI app (PhonePe / GPay / Paytm) under transaction details — &quot;UPI Ref No&quot; or &quot;UTR&quot;.</div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                data-testid="buy-at-payername-input"
                type="text" value={payerName} onChange={e => setPayerName(e.target.value)}
                placeholder="Your name (optional)"
                className="w-full px-3 py-2.5 rounded-lg bg-slate-950 border border-slate-700 text-slate-100"
              />
              <input
                data-testid="buy-at-payeracc-input"
                type="text" value={payerAcc} onChange={e => setPayerAcc(e.target.value)}
                placeholder="Your UPI / Account (optional)"
                className="w-full px-3 py-2.5 rounded-lg bg-slate-950 border border-slate-700 text-slate-100"
              />
            </div>
            <input
              data-testid="buy-at-notes-input"
              type="text" value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Notes for admin (optional)"
              className="w-full px-3 py-2.5 rounded-lg bg-slate-950 border border-slate-700 text-slate-100"
            />
            {/* Screenshot upload — speeds up admin verification */}
            <div className="rounded-lg border border-dashed border-amber-400/40 bg-slate-950/40 p-3">
              <div className="text-xs font-semibold text-amber-200 mb-2">📸 Payment Screenshot (optional but recommended)</div>
              {screenshotUrl ? (
                <div className="flex items-center gap-3">
                  <img src={screenshotUrl} alt="payment proof" className="w-16 h-16 rounded-md object-cover border border-slate-700" data-testid="buy-at-shot-preview" />
                  <div className="flex-1 text-xs text-slate-300 truncate">{screenshotUrl.split('/').pop()}</div>
                  <button onClick={() => setScreenshotUrl('')} data-testid="buy-at-shot-remove" className="px-2 py-1 rounded-md bg-slate-700 text-xs text-slate-200">Remove</button>
                </div>
              ) : (
                <label className="block cursor-pointer">
                  <div className="text-xs text-slate-400 px-3 py-2 rounded-md bg-slate-900 border border-slate-700 text-center hover:border-amber-400">
                    {uploadingShot ? 'Uploading…' : 'Click to upload payment confirmation image (PNG/JPG, max 8 MB)'}
                  </div>
                  <input
                    data-testid="buy-at-shot-input"
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={e => uploadScreenshot(e.target.files?.[0])}
                    className="hidden"
                    disabled={uploadingShot}
                  />
                </label>
              )}
              <div className="text-[10px] text-slate-500 mt-1.5">Helps admin verify your payment faster → quicker AT credit.</div>
            </div>
            <button
              data-testid="buy-at-submit-btn"
              disabled={submitting}
              onClick={submit}
              className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold disabled:opacity-60"
            >
              {submitting ? 'Submitting…' : 'Submit Deposit Request'}
            </button>
          </div>

          {/* History */}
          {history.length > 0 && (
            <div className="pt-3 border-t border-slate-800">
              <div className="text-xs font-semibold text-slate-300 mb-2">Your recent requests</div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {history.slice(0, 5).map(d => (
                  <div key={d.id} className="flex items-center justify-between text-xs bg-slate-950 rounded-lg p-2.5">
                    <div>
                      <div className="font-semibold text-slate-100">₹{d.amount_inr} → {d.at_equivalent} AT</div>
                      <div className="text-slate-500">Ref {d.txn_ref} · {d.method.toUpperCase()}</div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${d.status === 'approved' ? 'bg-emerald-500/20 text-emerald-300' : d.status === 'rejected' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'}`}>
                      {d.status.toUpperCase()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuyATBalanceModal;
